'use client';

import React, { useCallback } from 'react';

import { formatCurrency } from 'src/helpers/utils';
import type { ActivityLogEntry } from 'src/types';

interface InvoiceEventCardProps {
  logDetails: ActivityLogEntry;
}

const InvoiceEventCard: React.FC<InvoiceEventCardProps> = ({ logDetails }) => {
  const getLogMessage = useCallback(() => {
    const eventDetails = logDetails?.event_attributes;
    const amountFormatted = formatCurrency(Number(eventDetails?.amount) || 0, 2, 2);
    const categoryLabel = logDetails?.service_category;
    const status = eventDetails?.to?.value?.toLowerCase();

    switch (eventDetails?.to?.value) {
      case 'refunded':
      case 'voided':
        return `Invoice totaling ${amountFormatted} has been ${status}.`;
      case 'payment-skipped':
        return `Invoice totaling ${amountFormatted} has been covered by Lula under our warranty policy.`;
      case 'partially-paid-offline':
        return `Partial payment of ${formatCurrency(Number(eventDetails?.amount_paid)  || 0, 2,2)} received for invoice totaling ${amountFormatted}`;
      case 'paid-offline':
        return `Invoice totaling ${amountFormatted} has been paid in full.`;
      case 'invoiced':
        return `Invoice for ${categoryLabel?.label} (#${logDetails?.job_no}) totaling ${amountFormatted} has been created`;
      case 'invoice-paid':
        return `Invoice totaling ${amountFormatted} has been paid in full.`;
      default:
        return '';
    }
  }, [
    logDetails?.event_attributes,
    logDetails?.job_no,
    logDetails?.service_category,
  ]);

  const generateInvoiceLabel = useCallback((
    invoiceNumber: string | undefined,
  ): Record<string, string> => {
    const formattedInvoiceNumber = invoiceNumber ? ` #${invoiceNumber}` : '';
    return {
      'payment-skipped': `Invoice ${formattedInvoiceNumber} Covered by Warranty`,
      refunded: `Invoice ${formattedInvoiceNumber} Refunded`,
      voided: `Invoice ${formattedInvoiceNumber} Voided`,
      'past-due': `Invoice ${formattedInvoiceNumber} Past Due`,
      invoiced: `New Invoice ${formattedInvoiceNumber} Created`,
      'paid-offline': `Invoice ${formattedInvoiceNumber} Paid`,
      'partially-paid-offline': `Invoice ${formattedInvoiceNumber} Paid Partially`,
      'invoice-paid': `Invoice ${formattedInvoiceNumber} Paid`,
    };
  }, []);

  const eventAttrs = logDetails?.event_attributes;

  return (
    <React.Fragment>
      <p className="mb-0">
        <span>
          {generateInvoiceLabel(eventAttrs?.invoice_no)?.[eventAttrs?.to?.value]}
        </span>
      </p>
      <p>
        {getLogMessage()}
        {' '}
        {eventAttrs?.freshbooks_share_url ? (
          <a
            href={eventAttrs?.freshbooks_share_url}
            target="_blank"
            type="button"
            color="link"
            rel="noreferrer"
          >
            View PDF
          </a>
        ) : ''}
      </p>
    </React.Fragment>
  );
};

export default InvoiceEventCard;
